import { Component, inject, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { AuthService } from "@front/m19-services";
import { UntilDestroy } from "@ngneat/until-destroy";

import { Currency, Response, UserBase } from "@front/m19-api-client";
import { IAlertComponent, RegisterFormComponent, RegisterFormValue } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { Angulartics2 } from "angulartics2";
import { LogoComponent } from "@m19-board/shared/logo/logo.component";

@UntilDestroy()
@Component({
  selector: "app-register",
  templateUrl: "./register.component.html",
  standalone: true,
  imports: [IAlertComponent, RegisterFormComponent, TranslocoDirective, RouterLink, LogoComponent],
})
export class RegisterComponent {
  private authService = inject(AuthService);
  private router = inject(Router);
  private angulartics2 = inject(Angulartics2);
  private layoutSelector = inject(LayoutSelectorService);
  private route = inject(ActivatedRoute);

  termsAndConditionLink = this.layoutSelector.getTermsAndConditionLink();
  agency = this.layoutSelector.getAgencyName();

  loading = signal(false);
  error = signal<string | null>(null);

  shouldDisplayMarketingFields = signal(this.layoutSelector.getBoardType() === BoardType.M19);

  register(formValue: RegisterFormValue): void {
    if (!formValue) return;
    this.loading.set(true);
    const user = this.toUserBase(formValue);

    this.authService.register(user).subscribe({
      next: () => {
        this.angulartics2.eventTrack.next({
          action: "register",
          properties: { category: "register", label: "success_" + user.email },
        });
        this.goToHomeOrRedirect();
      },
      error: (err) => {
        this.error.set("User registration error: " + (err.response?.message ?? err.message));
        this.loading.set(false);
      },
    });
  }

  private toUserBase(formValue: RegisterFormValue): UserBase {
    const fullName: string = formValue.firstName.trim() + " " + formValue.lastName.trim();
    return {
      defaultCurrency: Currency.USD, // TODO select according to user accounts and browser language
      userName: fullName,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      companyName: formValue.companyName,
      amazonStoreFrontUrl: formValue.amazonStoreFrontUrl,
      monthlyAdSpend: formValue.monthlyAdSpend,
      howDidYouHearAboutUs: formValue.howDidYouHearAboutUs,
      email: formValue.email,
      newPassword: formValue.password,
      registered: true, // Already checked by the form
    };
  }

  goToHomeOrRedirect(): void {
    const redirectUrl = this.route.snapshot.queryParams["redirect"] ?? "dashboard";
    this.router.navigateByUrl(redirectUrl);
  }
}
